import { CITY_OPTIONS, getDistrictOptions } from '../constants/cityDistrictOptions';

export const DEFAULT_MEAL_PREFERENCES = {
  breakfast: { enabled: true, time: '08:00', label: 'Kahvaltı' },
  lunch: { enabled: true, time: '13:00', label: 'Öğle Yemeği' },
  dinner: { enabled: true, time: '19:30', label: 'Akşam Yemeği' }
};

export const REGISTRATION_SURVEY_QUESTIONS = [
  {
    id: 'q1',
    number: 1,
    label: 'Rutininiz?',
    type: 'radio',
    columns: 'grid-cols-2 md:grid-cols-4',
    options: [
      { label: 'Yoğun Çalışan', icon: 'work' },
      { label: 'Ev Hanımı/Erkeği', icon: 'home' },
      { label: 'Öğrenci', icon: 'school' },
      { label: 'Emekli', icon: 'chair' }
    ]
  },
  {
    id: 'q2',
    number: 2,
    label: 'Mutfak Beceri Seviyeniz?',
    type: 'radio',
    columns: 'grid-cols-1 md:grid-cols-3',
    options: [
      { label: 'Acemi Şef', icon: 'egg_alt' },
      { label: 'Orta Seviye', icon: 'tune' },
      { label: 'Mutfak Ustası', icon: 'verified' }
    ]
  },
  {
    id: 'q3',
    number: 3,
    label: 'Kaç Kişilik Yemek Pişiriyorsunuz?',
    type: 'radio',
    columns: 'grid-cols-5',
    compact: true,
    options: [{ label: '1' }, { label: '2' }, { label: '3' }, { label: '4' }, { label: '5+' }]
  },
  {
    id: 'q4',
    number: 4,
    label: 'Beslenme Tercihiniz?',
    type: 'radio',
    columns: 'grid-cols-2 md:grid-cols-4',
    options: [
      { label: 'Etçil', icon: 'kebab_dining' },
      { label: 'Vejetaryen', icon: 'spa' },
      { label: 'Pesketaryen', icon: 'set_meal' },
      { label: 'Vegan', icon: 'eco' }
    ]
  },
  {
    id: 'q5',
    number: 5,
    label: 'Gıda Hassasiyetiniz Var mı?',
    type: 'checkbox',
    columns: 'grid-cols-2 md:grid-cols-3',
    options: [
      { label: 'Yok' },
      { label: 'Laktoz' },
      { label: 'Gluten' },
      { label: 'Kuruyemiş' },
      { label: 'Yumurta' },
      { label: 'Balık' },
      { label: 'Soya' },
      { label: 'Susam' },
      { label: 'Deniz Ürünleri' }
    ]
  },
  {
    id: 'q7',
    number: 6,
    label: 'Mutfak Harcaması Takibi?',
    type: 'radio',
    columns: 'grid-cols-1 md:grid-cols-3',
    compact: true,
    options: [{ label: 'Evet' }, { label: 'Hayır ama istiyorum' }, { label: 'Hayır, gerek yok' }]
  },
  {
    id: 'q8',
    number: 7,
    label: 'Lütfen öğün ve bildirim (saat kaçta hatırlatalım) tercihlerinizi yapın?',
    type: 'meal_preferences'
  }
];

export function cloneMealPreferences(source = DEFAULT_MEAL_PREFERENCES) {
  return {
    breakfast: { ...DEFAULT_MEAL_PREFERENCES.breakfast, ...(source?.breakfast || {}) },
    lunch: { ...DEFAULT_MEAL_PREFERENCES.lunch, ...(source?.lunch || {}) },
    dinner: { ...DEFAULT_MEAL_PREFERENCES.dinner, ...(source?.dinner || {}) }
  };
}

function normalizeServingValue(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (['1 Kişi', '1 kişi'].includes(text)) return '1';
  if (['2 Kişi', '2 kişi'].includes(text)) return '2';
  if (['3 Kişi', '3 kişi', '3-4 Kişi', '3-4 kişi'].includes(text)) return '3';
  if (['4 Kişi', '4 kişi'].includes(text)) return '4';
  if (['5+ Kişi', '5+ kişi'].includes(text)) return '5+';
  return text;
}

export function normalizeMealPreferences(raw) {
  if (!raw) return cloneMealPreferences();

  if (typeof raw === 'object' && !Array.isArray(raw) && (raw.breakfast || raw.lunch || raw.dinner)) {
    return cloneMealPreferences({
      breakfast: {
        enabled: raw?.breakfast?.enabled !== false,
        time: String(raw?.breakfast?.time || DEFAULT_MEAL_PREFERENCES.breakfast.time)
      },
      lunch: {
        enabled: raw?.lunch?.enabled !== false,
        time: String(raw?.lunch?.time || DEFAULT_MEAL_PREFERENCES.lunch.time)
      },
      dinner: {
        enabled: raw?.dinner?.enabled !== false,
        time: String(raw?.dinner?.time || DEFAULT_MEAL_PREFERENCES.dinner.time)
      }
    });
  }

  const values = Array.isArray(raw) ? raw.map(String) : [String(raw)];
  const hasBreakfast = values.some((v) => v.includes('Sabah') || v.includes('09:00') || v.includes('08:00'));
  const hasLunch = values.some((v) => v.includes('Öğle') || v.includes('Ogle') || v.includes('13:00'));
  const hasDinner = values.some((v) => v.includes('Akşam') || v.includes('Aksam') || v.includes('17:00') || v.includes('18:00') || v.includes('19:30'));

  return cloneMealPreferences({
    breakfast: { enabled: hasBreakfast },
    lunch: { enabled: hasLunch },
    dinner: { enabled: hasDinner }
  });
}

export function normalizeRegistrationSurveyAnswers(intakeSurvey = {}) {
  const source = intakeSurvey?.answers && typeof intakeSurvey.answers === 'object'
    ? { ...intakeSurvey.answers }
    : { ...(intakeSurvey || {}) };
  const normalized = {
    q1: source.q1 || source.jobTitle || '',
    q2: source.q2 || source.skillLevel || '',
    q3: normalizeServingValue(source.q3 || source.servingSize || source.serving || ''),
    q4: source.q4 || source.diet || '',
    q5: Array.isArray(source.q5)
      ? source.q5
      : Array.isArray(source.preferences)
        ? source.preferences
        : source.q5
          ? [source.q5]
          : source.preferences
            ? [source.preferences]
            : [],
    q6: source.q6 || source.frequency || intakeSurvey?.frequency || intakeSurvey?.answers?.frequency || '',
    q7: source.q7 || source.tracking || '',
    q8: normalizeMealPreferences(source.q8 || source.mealPreferences || source.notificationPreferences || source.reminderTimes || source.sendTime),
    city: String(source.city || intakeSurvey.city || intakeSurvey?.specialDaysContact?.city || '').trim(),
    district: String(source.district || intakeSurvey.district || intakeSurvey?.specialDaysContact?.district || '').trim()
  };

  return normalized;
}

export function buildLegacyReminderArray(mealPreferences) {
  const values = [];
  if (mealPreferences?.breakfast?.enabled) values.push('Sabah');
  if (mealPreferences?.lunch?.enabled) values.push('Öğle');
  if (mealPreferences?.dinner?.enabled) values.push('Akşam');
  return values;
}

export function buildReminderTimesArray(mealPreferences) {
  return ['breakfast', 'lunch', 'dinner']
    .filter((slot) => mealPreferences?.[slot]?.enabled)
    .map((slot) => String(mealPreferences?.[slot]?.time || '').trim())
    .filter(Boolean);
}

export function buildRegistrationIntakeSurveyPayload(rawAnswers = {}, extras = {}) {
  const normalized = normalizeRegistrationSurveyAnswers({ answers: rawAnswers });
  const reminderTimes = buildReminderTimesArray(normalized.q8);
  const legacyReminderChoices = buildLegacyReminderArray(normalized.q8);

  const cleanedAnswers = {
    q1: normalized.q1,
    q2: normalized.q2,
    q3: normalized.q3,
    q4: normalized.q4,
    q5: normalized.q5,
    q6: normalized.q6,
    q7: normalized.q7,
    q8: cloneMealPreferences(normalized.q8),
    jobTitle: normalized.q1,
    skillLevel: normalized.q2,
    serving: normalized.q3,
    servingSize: normalized.q3,
    diet: normalized.q4,
    preferences: normalized.q5,
    frequency: normalized.q6,
    cookingFrequency: normalized.q6,
    tracking: normalized.q7,
    mealPreferences: cloneMealPreferences(normalized.q8),
    notificationPreferences: cloneMealPreferences(normalized.q8),
    reminderTimes,
    legacyReminderChoices,
    sendTime: reminderTimes[0] || '',
    city: normalized.city,
    district: normalized.district
  };

  const intakeSurvey = {
    ...extras,
    answers: cleanedAnswers,
    preferences: normalized.q5,
    servingSize: normalized.q3,
    jobTitle: normalized.q1,
    skillLevel: normalized.q2,
    diet: normalized.q4,
    frequency: normalized.q6,
    cookingFrequency: normalized.q6,
    tracking: normalized.q7,
    mealPreferences: cloneMealPreferences(normalized.q8),
    notificationPreferences: cloneMealPreferences(normalized.q8),
    reminderTimes,
    legacyReminderChoices,
    sendTime: reminderTimes[0] || '',
    city: normalized.city,
    district: normalized.district
  };

  if (normalized.city || normalized.district) {
    intakeSurvey.specialDaysContact = {
      city: normalized.city,
      district: normalized.district
    };
  }

  return intakeSurvey;
}

export function applyMealPreferencesToPlanningConfig(baseConfig, mealPreferences) {
  const next = { ...baseConfig };
  const normalizedMeals = normalizeMealPreferences(mealPreferences);
  const breakfast = normalizedMeals.breakfast;
  const lunch = normalizedMeals.lunch;
  const dinner = normalizedMeals.dinner;

  next.notifications = {
    ...(next.notifications || {}),
    breakfast_workday: breakfast.time || '08:00',
    breakfast_weekend: breakfast.time || '08:00',
    auto_menu_workday: (dinner.enabled !== false ? dinner.time : lunch.enabled !== false ? lunch.time : breakfast.time) || '19:30',
    auto_menu_weekend: (dinner.enabled !== false ? dinner.time : lunch.enabled !== false ? lunch.time : breakfast.time) || '19:30',
    send_weekend: true,
    send_weekend_auto_menu: true
  };
  next.hasSetAutoMenuTiming = true;
  next.skipsBreakfast = breakfast.enabled === false;
  return next;
}

export function getLocationOptions(city) {
  return {
    cities: CITY_OPTIONS,
    districts: getDistrictOptions(city)
  };
}
